/**
 * Root layout — Expo Router entry.
 *
 * Responsibilities:
 *  - Wrap the entire app in QueryClientProvider (TanStack Query) and
 *    SafeAreaProvider (react-native-safe-area-context).
 *  - Call session.restore() on mount to load the persisted auth token.
 *  - Redirect to /login when unauthenticated, /(app) when authenticated.
 *    While status is 'loading' render nothing (avoids flash of wrong screen).
 */

import { useCallback, useEffect } from 'react';
import { Platform, View } from 'react-native';
import { Slot, useRouter, useSegments, useRootNavigationState } from 'expo-router';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { StatusBar } from 'expo-status-bar';
import * as SplashScreen from 'expo-splash-screen';
import { useSession } from '../src/auth/session';
import { getLicense } from '../src/api/account';
import { useAppFonts } from '../src/theme/fonts';
import { theme } from '../src/theme';
import { HelpProvider } from '../src/help';
import { AssistantDockProvider } from '../src/components/assistant/AssistantDockProvider';
import { GlobalAssistantDock } from '../src/components/assistant/GlobalAssistantDock';
import { VersionGate } from '../src/components/VersionGate';
import { initSentry, wrap as sentryWrap } from '../src/observability/sentry';

const IS_NATIVE = Platform.OS !== 'web';

// Error monitoring — must run before the app renders. No-op unless a Sentry DSN is configured.
initSentry();

// Hold the native splash until fonts are ready (no system-font flash).
void SplashScreen.preventAutoHideAsync();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

function AuthGate() {
  const { status } = useSession();
  const router = useRouter();
  const segments = useSegments();
  // The root navigator must be mounted before any imperative navigation, or Expo Router
  // throws "Attempted to navigate before mounting the Root Layout". navState.key is set once
  // the <Slot/> below has mounted — gate all redirects on it.
  const navState = useRootNavigationState();

  // In-app subscriptions (2026-06-19): once authenticated, read the license so the gate can route a
  // LOCKED account to the right place. Native store/incomplete accounts → /paywall (RevenueCat);
  // stripe accounts → the billing recovery screen. Web keeps its existing behavior (no native paywall).
  const license = useQuery({
    queryKey: ['license'],
    queryFn: getLicense,
    enabled: status === 'authenticated',
  });

  useEffect(() => {
    if (!navState?.key) return; // navigator not mounted yet
    if (status === 'loading') return;

    // The platform-admin console (web-only) runs its OWN auth gate (useAdminSession). The tenant gate
    // must ignore it entirely, or it would bounce an admin to /login or /(app).
    if (segments[0] === 'admin') return;

    const isWeb = Platform.OS === 'web';
    const seg0 = segments[0];
    const onApp = seg0 === '(app)';
    // getabeam.com marketing site — a PUBLIC, web-only route group whose root ('/') is the
    // landing page. Logged-in users may still browse it (e.g. /pricing) to upgrade.
    const onMarketing = seg0 === '(marketing)';
    const onMarketingIndex = onMarketing && segments.length === 1; // the bare landing at '/'
    const onLogin = seg0 === 'login';
    const onSignup = seg0 === 'signup'; // in-app account creation (native)
    const onPaywall = seg0 === 'paywall'; // subscription gate (native)
    // WO-D2.M — the set-password invite page is PUBLIC (owner has no session yet).
    // signup is also reachable while unauthenticated (it creates the session).
    // forgot-password / reset-password are PUBLIC too: the user has no session when recovering it
    // (reset-password is opened from an emailed link / deep link).
    const onPublicAuth =
      onLogin || onSignup || seg0 === 'accept-invite' || seg0 === 'welcome' || seg0 === 'forgot-password' || seg0 === 'reset-password';

    if (status === 'authenticated') {
      // Send signed-in users from the login/signup screens and the bare landing into the app; on
      // native the marketing surface never applies. Otherwise allow it (so owners can reach
      // /pricing to upgrade and /download from inside a session).

      // --- Subscription gate (native only) -------------------------------------------------
      // A LOCKED account (status not active/trialing) must subscribe before using the app.
      // Wait for the license read before routing so we don't bounce through /home.
      if (IS_NATIVE && license.data && !license.data.active) {
        const manage = license.data.manage;
        // A brand-new self-signup is created status='incomplete' (never purchased): it has NO billing
        // relationship to recover, so it must always go to the paywall to make its first purchase —
        // never the Stripe billing screen, whose portal 502s with "no Stripe customer on file".
        const neverPurchased = license.data.status === 'incomplete';
        // Stripe-billed locked accounts that lapsed (have a real subscription) recover via the billing
        // screen. An incomplete account never qualifies, regardless of the reported manage hint.
        if (manage === 'stripe_portal' && !neverPurchased) {
          if (!segments.includes('billing')) router.replace('/(app)/account/billing');
          return;
        }
        // Store / incomplete accounts → the RevenueCat paywall gate.
        if (!onPaywall) router.replace('/paywall');
        return;
      }

      // On native, while the license read is still in flight, don't bounce off the signup/paywall
      // screens (we don't yet know if the account is locked). Once it resolves the block above
      // routes a locked account; an active one falls through here.
      if (IS_NATIVE && (onSignup || onPaywall) && !license.data) {
        return;
      }

      // Active (or web) account on an auth/landing screen → into the app. Also leave the paywall
      // once the license is active.
      if (onLogin || onSignup || onPaywall || onMarketingIndex || (!isWeb && onMarketing)) {
        router.replace('/home');
      }
      return;
    }

    // status === 'unauthenticated'
    if (isWeb) {
      // Marketing + the public auth pages render anonymously; the protected app falls back to login.
      if (onApp) router.replace('/login');
    } else {
      // Native has no marketing surface — only the public auth pages render; everything else → login.
      if (!onPublicAuth) router.replace('/login');
    }
  }, [status, segments, router, navState?.key, license.data]);

  // ALWAYS render the navigator on the first render (do not return null before <Slot/>),
  // otherwise the redirect above fires before the Root Layout mounts.
  return <Slot />;
}

/**
 * The global assistant dock (design doc §6). Rendered ONLY when authenticated, so the
 * "Ask BEAM" entry floats over EVERY authed route — the tabs AND any pushed detail
 * screen — because it is a sibling of the navigator (<Slot/>) at the root, not inside
 * any one screen. Unauthenticated (login/marketing/paywall) renders nothing.
 */
function AuthedAssistantDock() {
  const status = useSession((s) => s.status);
  if (status !== 'authenticated') return null;
  return <GlobalAssistantDock />;
}

function RootLayout() {
  const restore = useSession((s) => s.restore);
  const [fontsLoaded, fontError] = useAppFonts();

  useEffect(() => {
    restore();
  }, [restore]);

  // Reveal the app (hide splash) only once fonts are ready, so first paint uses the
  // brand typefaces. If fonts fail to load, proceed rather than trap the user on splash.
  const onReady = useCallback(async () => {
    if (fontsLoaded || fontError) {
      await SplashScreen.hideAsync();
    }
  }, [fontsLoaded, fontError]);

  if (!fontsLoaded && !fontError) {
    return null; // splash stays up
  }

  return (
    <QueryClientProvider client={queryClient}>
      <SafeAreaProvider>
        <HelpProvider>
          <AssistantDockProvider>
            <View style={{ flex: 1, backgroundColor: theme.color.bg.app }} onLayout={onReady}>
              <StatusBar style="dark" backgroundColor={theme.color.bg.app} />
              {/* REM-MOB-03 — force-update gate: an out-of-date binary (below the backend's declared
                  minimum) is blocked here and never reaches the app surface. Fails OPEN on a network
                  hiccup so a down /health can't lock everyone out. */}
              <VersionGate>
                <AuthGate />
                {/* Global assistant dock — over every authed route (design doc §6). */}
                <AuthedAssistantDock />
              </VersionGate>
            </View>
          </AssistantDockProvider>
        </HelpProvider>
      </SafeAreaProvider>
    </QueryClientProvider>
  );
}

// Wrap the root so Sentry installs its React error boundary (captures render crashes). When Sentry is
// disabled (no DSN), wrap is a passthrough.
export default sentryWrap(RootLayout);
